/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

/* eslint-disable @typescript-eslint/no-non-null-assertion */

import {ClientSideRowModelModule} from "@ag-grid-community/client-side-row-model";
import {ColDef, GridOptions, ICellRendererParams, Module} from "@ag-grid-community/core";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {FormGroupDirective, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {RegistryContexts} from "@app/models/constants/registry-constants";
import {DATE_FMT} from "@app/models/date-fmt";
import {generateUuid} from "@app/models/functions/generate-uuid.function";
import {CommonState} from "@app/store/common/common.state";
import {ONCO_DRUGS} from "@doctor-panel-modules/patient-treatment-plan/models/onco-drugs";
import {environment} from "@env/environment";
import {Store} from "@ngxs/store";
import {
  TrecMetadata,
  TrecTherapy,
  TrecTherapyAssumptionData,
  TrecVariableAssumption,
} from "@trec/api/clinical-data";
import {AG_GRID_LOCALE_IT} from "@trec/components/table";
import {addDays, endOfDay, format, startOfDay} from "date-fns";
import {it} from "date-fns/locale";
import {Subscription} from "rxjs";
import {CdItemFormComponent, CdItemFormMode} from "../cd-item-form-component";

interface FormTherapyData {
  startDate: string | null;

  capecitabineData: {
    cycleDuration: number | null;
    cycleInterval: number | null;
    cycleNumber: number | null;
    dosePercentage: number | null;
    bodyArea: number | null;
    dose: number | null;
    morningAssumptionTime: string | null;
    afternoonAssumptionTime: string | null;
  };

  notes: string | null;
}

type FormTherapyDataKeys = {[key in keyof FormTherapyData]: any};

interface CalcCapacitabineParams {
  actualDose: number;
  endDate: Date;
  assumptionsCapecitabine150: TrecVariableAssumption[];
  assumptionsCapecitabine500: TrecVariableAssumption[];

  capecitabine150NotEmpty: boolean;
  capecitabine500NotEmpty: boolean;
}

interface CapacitabineTableData {
  day: Date;
  assumptions: {
    capecitabine150: TrecTherapyAssumptionData[];
    capecitabine500: TrecTherapyAssumptionData[];
  };
}

@Component({
  selector: "c3-form-therapy-oncological",
  templateUrl: "./form-therapy-oncological.component.html",
  styleUrls: ["./form-therapy-oncological.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FormTherapyOncologicalComponent
  extends CdItemFormComponent<TrecTherapy[], FormTherapyData>
  implements OnInit, OnDestroy {
  // IO ///////////////////////////////////////////////////////////////////////////////////////////

  @Input()
  public get mode(): CdItemFormMode {
    return super.mode;
  }
  public set mode(value: CdItemFormMode) {
    super.mode = value;
  }

  @Input()
  public get item(): TrecTherapy[] | undefined {
    return super.item;
  }
  public set item(value: TrecTherapy[] | undefined) {
    super.item = value;
  }

  /**
   * Whether it's necessary to add at least one dosage or not.
   */
  private _context?: RegistryContexts | undefined | null;
  @Input()
  public get context(): RegistryContexts | undefined | null {
    return this._context;
  }
  public set context(value: RegistryContexts | undefined | null) {
    this._context = value;
    // Update form
    this._update$.next(true);
  }

  // Component elems /////////////////////////////////////////////////////////////////////////////

  @ViewChild("formElem", {read: FormGroupDirective, static: true}) formElem: FormGroupDirective;

  // Defaults and Enums ///////////////////////////////////////////////////////////////////////////

  readonly ONCO_DRUGS = ONCO_DRUGS;

  /**
   * Avoid error 'theraphy' cannot start in the past, starting from today.
   */
  readonly today = new Date();
  readonly todayStr = format(this.today, DATE_FMT);

  // Form fields //////////////////////////////////////////////////////////////////////////////////

  readonly form: UntypedFormGroup;

  get capacitabineForm(): UntypedFormGroup {
    return this.form.get("capecitabineData") as UntypedFormGroup;
  }

  calculatedParams: CalcCapacitabineParams | null = null;

  // AgGrid fields ////////////////////////////////////////////////////////////////////////////////

  // Angular
  agModules: Module[] = [ClientSideRowModelModule];

  agGridOptions: GridOptions = {
    localeText: AG_GRID_LOCALE_IT,
    context: {componentParent: this},
    defaultColDef: {sortable: false, filter: false, resizable: true, suppressMovable: true},
    suppressCellFocus: true,
    domLayout: "autoHeight",
  };

  columnDefs: ColDef[] = [
    {
      headerName: `Giorno`,
      field: "date",
      colId: "day",
      flex: 20,
      minWidth: 160,
      cellRenderer: (params: ICellRendererParams) => {
        const rowIndex = params.node.rowIndex;
        if (rowIndex) {
          return `Giorno ${rowIndex + 1} (${format(
            (params.data as CapacitabineTableData).day,
            "dd/MM",
            {locale: it}
          )})`
        }

      },
    },
    {
      headerName: `Mattino (500mg)`,
      colId: "assumptionMorning500",
      flex: 20,
      minWidth: 180,
      cellRenderer: (params: ICellRendererParams) =>
        `${(params.data as CapacitabineTableData).assumptions.capecitabine500[0]?.quantity} cp.`,
    },
    {
      headerName: `Mattino (150mg)`,
      colId: "assumptionMorning150",
      flex: 20,
      minWidth: 180,
      cellRenderer: (params: ICellRendererParams) =>
        `${(params.data as CapacitabineTableData).assumptions.capecitabine150[0]?.quantity} cp.`,
    },
    {
      headerName: `Pomeriggio (500mg)`,
      colId: "assumptionAfternoon500",
      flex: 20,
      minWidth: 180,
      cellRenderer: (params: ICellRendererParams) =>
        `${(params.data as CapacitabineTableData).assumptions.capecitabine500[1]?.quantity} cp.`,
    },
    {
      headerName: `Pomeriggio (150mg)`,
      colId: "assumptionAfternoon150",
      flex: 20,
      minWidth: 180,
      cellRenderer: (params: ICellRendererParams) =>
        `${(params.data as CapacitabineTableData).assumptions.capecitabine150[1]?.quantity} cp.`,
    },
  ];

  rowData: CapacitabineTableData[] = [];

  private readonly subscr: Subscription = new Subscription();

  constructor(private fb: UntypedFormBuilder, protected store: Store, protected cd: ChangeDetectorRef) {
    super(cd);

    // Initialize form schema
    this.form = this.fb.group(
      {
        startDate: [format(this.today, DATE_FMT), Validators.required], // Date

        notes: [""],

        capecitabineData: this.fb.group({
          cycleDuration: this.fb.control(null, [Validators.required, Validators.min(1)]),
          cycleInterval: this.fb.control(null, [Validators.required, Validators.min(0)]),
          cycleNumber: this.fb.control(null, [Validators.required, Validators.min(1)]),
          dosePercentage: this.fb.control(null, [
            Validators.required,
            Validators.min(1),
            Validators.max(100),
          ]),
          bodyArea: this.fb.control(null, [Validators.required, Validators.min(0)]),
          dose: this.fb.control(null, [Validators.required, Validators.min(0)]),
          morningAssumptionTime: this.fb.control(null, [Validators.required]),
          afternoonAssumptionTime: this.fb.control(null, [Validators.required]),
        }),
      } as FormTherapyDataKeys
      // {
      //   validators: [
      //     (_control) =>  // Custom validator to ensure that exists at least a therapy with at least one assumption with dose != 0
      //       this.calculatedParams?.capecitabine150NotEmpty || this.calculatedParams?.capecitabine500NotEmpty ?
      //         null :
      //         {'missing_assumptions': 'There are no therapies with at least one assumption with dose != 0'}
      //   ]
      // }
    );
  }

  ngOnInit() {
    this.subscr.add(
      this.form.valueChanges.subscribe(() => {
        if (this.form.valid) {
          const formValue = this.form.value as FormTherapyData;
          const startDate = new Date(formValue.startDate!);

          const newParams: CalcCapacitabineParams = {
            actualDose: 0,
            endDate: this.calculateEndDate()!,
            assumptionsCapecitabine150: [],
            assumptionsCapecitabine500: [],
            capecitabine150NotEmpty: false,
            capecitabine500NotEmpty: false,
          };

          for (let cycle = 0; cycle < formValue.capecitabineData.cycleNumber!; cycle++) {
            const cycleDate = addDays(
              startDate,
              cycle *
              (formValue.capecitabineData.cycleDuration! +
                formValue.capecitabineData.cycleInterval!)
            );

            const cycleParams = this.calculateAssumptions(
              cycleDate,
              formValue.capecitabineData.cycleDuration!,
              formValue.capecitabineData.dose!,
              formValue.capecitabineData.bodyArea!,
              formValue.capecitabineData.dosePercentage!,
              formValue.capecitabineData.morningAssumptionTime!,
              formValue.capecitabineData.afternoonAssumptionTime!
            );

            newParams.actualDose = cycleParams.actualDose;
            newParams.assumptionsCapecitabine150 = [
              ...newParams.assumptionsCapecitabine150,
              ...cycleParams.assumptionsCapecitabine150,
            ];
            newParams.assumptionsCapecitabine500 = [
              ...newParams.assumptionsCapecitabine500,
              ...cycleParams.assumptionsCapecitabine500,
            ];
          }

          newParams.capecitabine150NotEmpty = this.hasAnyAssumptionWithDose(
            newParams.assumptionsCapecitabine150
          );
          newParams.capecitabine500NotEmpty = this.hasAnyAssumptionWithDose(
            newParams.assumptionsCapecitabine500
          );

          this.calculatedParams = newParams;
          // Copy new assumptions in table data
          this.rowData = this.calculatedParams.assumptionsCapecitabine150.map(
            (assumptions150, i) => ({
              day: assumptions150.date,
              assumptions: {
                capecitabine150: assumptions150.assumptions ?? [],
                capecitabine500:
                  this.calculatedParams?.assumptionsCapecitabine500[i].assumptions ?? [],
              },
            })
          );
        } else {
          this.calculatedParams = null;
          this.rowData = [];
        }

        this.cd.markForCheck();
      })
    );
  }

  protected resetDefaultValues() {
    this.form.patchValue({
      startDate: format(this.today, DATE_FMT), // Date

      notes: "",

      capecitabineData: {
        cycleDuration: null,
        cycleInterval: null,
        cycleNumber: null,
        dosePercentage: null,
        bodyArea: null,
        dose: null,
        morningAssumptionTime: null,
        afternoonAssumptionTime: null,
      },
    } as FormTherapyData);
  }

  // Callbacks ////////////////////////////////////////////////////////////////////////////////////

  prepareData(): TrecTherapy[] | null {
    if (!this.calculatedParams) {
      return null;
    }

    // Note: Works only in insert mode (not edit)

    const metadata: TrecMetadata = {
      start_date: startOfDay(new Date(this.formValue.startDate!)).valueOf(),
      end_date: endOfDay(this.calculatedParams.endDate).valueOf(),
      source: environment.appId,
      provenance: "medical",
      device: undefined,
    };

    const therapy150 = this.calculatedParams.capecitabine150NotEmpty
      ? TrecTherapy.create(
        {
          id: undefined,
          patientId: this.store.selectSnapshot(CommonState.patientId) ?? "",
        },
        {...metadata},
        {
          drug: this.ONCO_DRUGS.find((d) => d._id === "035219017")!, // Xeloda 150mg
          state: "active",
          options: [],
          regular: {
            // Definition of regular assumption is mandatory (but here, we do not need it)
            assumptions: [],
            frequency: {
              type: "every_x_days",
              values: [], // Every 0 days
            },
          },
          variable: {
            assumptions: TrecTherapy.calcVariableAssumptions({
              therapyStartDate: new Date(metadata.start_date),
              assumptionsPerDay: this.calculatedParams.assumptionsCapecitabine150,
            })
              // Remove assumptions with dose == 0
              .map((dailyAssumptions) => dailyAssumptions.filter((a) => a.quantity !== 0)),
          },
          notes: this.formValue.notes ?? "",
        }
      )
      : null;

    const therapy500 = this.calculatedParams.capecitabine500NotEmpty
      ? TrecTherapy.create(
        {
          id: undefined,
          patientId: this.store.selectSnapshot(CommonState.patientId) ?? "",
        },
        {...metadata},
        {
          drug: this.ONCO_DRUGS.find((d) => d._id === "035219029")!, // Xeloda 500mg
          state: "active",
          options: [],
          regular: {
            // Definition of regular assumption is mandatory (but here, we do not need it)
            assumptions: [],
            frequency: {
              type: "every_x_days",
              values: [], // Every 0 days
            },
          },
          variable: {
            assumptions: TrecTherapy.calcVariableAssumptions({
              therapyStartDate: new Date(metadata.start_date),
              assumptionsPerDay: this.calculatedParams.assumptionsCapecitabine500,
            })
              // Remove assumptions with dose == 0
              .map((dailyAssumptions) => dailyAssumptions.filter((a) => a.quantity !== 0)),
          },
          notes: this.formValue.notes ?? "",
        }
      )
      : null;

    return [therapy150, therapy500].filter((t) => !!t) as TrecTherapy[];
  }

  update() {
    super.update();

    this.resetDefaultValues();

    // Note: Works only in insert mode (not edit)

    // Set default values
    this.form.patchValue({
      startDate: format(this.today, DATE_FMT),
      notes: "",

      capecitabineData: {
        cycleDuration: null,
        cycleInterval: null,
        cycleNumber: null,
        dosePercentage: null,
        bodyArea: null,
        dose: null,
        morningAssumptionTime: null,
        afternoonAssumptionTime: null,
      },
    } as FormTherapyDataKeys);

    this.cd.markForCheck();
  }

  // Assumption algorithm /////////////////////////////////////////////////////////////////////////

  private calculateEndDate(): Date | null {
    if (this.form.invalid) {
      return null;
    }

    const formValue = this.form.value as FormTherapyData;

    if (!formValue.startDate) {
      return null;
    }

    const startDate = startOfDay(new Date(formValue.startDate));

    const effectiveTherapyDays =
      formValue.capecitabineData.cycleDuration! * formValue.capecitabineData.cycleNumber!;
    const intervalTherapyDays =
      formValue.capecitabineData.cycleInterval! *
      (formValue.capecitabineData.cycleNumber! > 0
        ? formValue.capecitabineData.cycleNumber! - 1
        : formValue.capecitabineData.cycleNumber!);

    return addDays(startDate, effectiveTherapyDays + intervalTherapyDays);
  }

  private calculateAssumptions(
    dataInizio: Date,
    durataGG: number, // therapy duration
    FL_DOSAGGIO: number, // dose
    FL_SUPERFICIE: number, // bodyArea
    cSH_RIDUZ: number, // dosePercentage
    morningAssumptionTime: string,
    afternoonAssumptionTime: string
  ): CalcCapacitabineParams {
    // Buckets per day
    const res150: {[day: string]: TrecTherapyAssumptionData[] | null} = {}; // Xeloda 150mg
    const res500: {[day: string]: TrecTherapyAssumptionData[] | null} = {}; // Xeloda 500mg

    let cShPastiglie150 = 0;
    let cShPastiglie500 = 0;
    let cSH_RESTOPREC = 0;
    let mattino500: number;
    let mattino150: number;
    let pomeriggio500: number;
    let pomeriggio150: number;

    let cFL_DOSE_EFF: number;

    const FL_DOSE = FL_DOSAGGIO * FL_SUPERFICIE;
    cFL_DOSE_EFF = parseInt(`${(FL_DOSE * cSH_RIDUZ) / 100.0}`, 10);

    for (let giorno = 0; giorno < durataGG; giorno++) {
      const FL_DOSE_EFF = this.fn_get_doseeff(cFL_DOSE_EFF, FL_SUPERFICIE, cSH_RESTOPREC);

      cShPastiglie150 = this.fn_get_pastiglie150(cFL_DOSE_EFF, FL_SUPERFICIE, cSH_RESTOPREC);
      cShPastiglie500 = this.fn_get_pastiglie500(cFL_DOSE_EFF, FL_SUPERFICIE, cSH_RESTOPREC);

      if (cShPastiglie500 % 2 && cShPastiglie150 % 2) {
        mattino500 = parseInt(`${(cShPastiglie500 + 1) / 2}`, 10);
        mattino150 = parseInt(`${cShPastiglie150 / 2}`, 10);
        pomeriggio500 = parseInt(`${cShPastiglie500 / 2}`, 10);
        pomeriggio150 = parseInt(`${(cShPastiglie150 + 1) / 2}`, 10);
      } else {
        mattino500 = parseInt(`${(cShPastiglie500 + 1) / 2}`, 10);
        mattino150 = parseInt(`${(cShPastiglie150 + 1) / 2}`, 10);
        pomeriggio500 = parseInt(`${cShPastiglie500 / 2}`, 10);
        pomeriggio150 = parseInt(`${cShPastiglie150 / 2}`, 10);
      }

      const assumptionDay = addDays(startOfDay(dataInizio), giorno).toISOString();

      res150[assumptionDay] = [
        {
          _id: generateUuid(),
          quantity: mattino150,
          unit: "tablet",
          time: morningAssumptionTime,
          moment: "morning",
        },
        {
          _id: generateUuid(),
          quantity: pomeriggio150,
          unit: "tablet",
          time: afternoonAssumptionTime,
          moment: "afternoon",
        },
      ];

      res500[assumptionDay] = [
        {
          _id: generateUuid(),
          quantity: mattino500,
          unit: "tablet",
          time: morningAssumptionTime,
          moment: "morning",
        },
        {
          _id: generateUuid(),
          quantity: pomeriggio500,
          unit: "tablet",
          time: afternoonAssumptionTime,
          moment: "afternoon",
        },
      ];

      cSH_RESTOPREC = cSH_RESTOPREC + cFL_DOSE_EFF - FL_DOSE_EFF;
    }

    const assumptionsCapecitabine150 = Object.entries(res150).map(([dateStr, assumptions]) => ({
      date: new Date(dateStr),
      assumptions,
    }));
    const assumptionsCapecitabine500 = Object.entries(res500).map(([dateStr, assumptions]) => ({
      date: new Date(dateStr),
      assumptions,
    }));

    return {
      assumptionsCapecitabine150,
      assumptionsCapecitabine500,
      actualDose: cFL_DOSE_EFF,
      endDate: this.calculateEndDate()!,

      capecitabine150NotEmpty: false, // Unused
      capecitabine500NotEmpty: false, // Unused
    };
  }

  private fn_get_pastiglie500(
    cFL_DOSE: number,
    cFL_SUPERFICIE: number,
    cSH_RESTOPREC: number
  ): number {
    const cFLDOSE_TOT = cFL_DOSE + cSH_RESTOPREC;
    /*CALCOLO IL NUMERO MASSIMO DI PASTIGLIE DA 500 POSSIBILE  */
    const cSH_PASTIGLIE500 = parseInt(`${cFLDOSE_TOT / 500}`, 10);

    return cSH_PASTIGLIE500;
  }

  private fn_get_pastiglie150(
    cFL_DOSE: number,
    cFL_SUPERFICIE: number,
    cSH_RESTOPREC: number
  ): number {
    const cFLDOSE_TOT = cFL_DOSE + cSH_RESTOPREC;
    /*CALCOLO IL NUMERO MASSIMO DI PASTIGLIE DA 500 POSSIBILE  */
    const cSH_PASTIGLIE500 = parseInt(`${cFLDOSE_TOT / 500}`, 10);

    /*CALCOLO LA DOSE OTTENUTA CON N PASTIGLIE DA 500*/
    const cFLDOSE_P500 = 500 * cSH_PASTIGLIE500;

    /* CALCOLO LA DOSE RIMANENTE DA COPRIRE CON PASTIGLIE DA 150*/
    const cFLDOSE_PER150 = cFLDOSE_TOT - cFLDOSE_P500;

    /*CALCOLO IL NUMERO MASSIMO DI PASTIGLIE DA 150 POSSIBILE  */
    const cSH_PASTIGLIE150 = parseInt(`${cFLDOSE_PER150 / 150}`, 10);

    return cSH_PASTIGLIE150;
  }

  private fn_get_doseeff(cFL_DOSE: number, cFL_SUPERFICIE: number, cSH_RESTOPREC: number): number {
    const cFLDOSE_TOT = cFL_DOSE + cSH_RESTOPREC;
    /*CALCOLO IL NUMERO MASSIMO DI PASTIGLIE DA 500 POSSIBILE  */
    const cSH_PASTIGLIE500 = parseInt(`${cFLDOSE_TOT / 500}`, 10);

    /*CALCOLO LA DOSE OTTENUTA CON N PASTIGLIE DA 500*/
    const cFLDOSE_P500 = 500 * cSH_PASTIGLIE500;

    /* CALCOLO LA DOSE RIMANENTE DA COPRIRE CON PASTIGLIE DA 150*/
    const cFLDOSE_PER150 = cFLDOSE_TOT - cFLDOSE_P500;

    /*CALCOLO IL NUMERO MASSIMO DI PASTIGLIE DA 150 POSSIBILE  */
    const cSH_PASTIGLIE150 = parseInt(`${cFLDOSE_PER150 / 150}`, 10);

    /*CALCOLO LA DOSE OTTENUTA CON N PASTIGLIE DA 150*/
    const cFLDOSE_P150 = 150 * cSH_PASTIGLIE150;

    const cFL_DOSEEFF = cFLDOSE_P500 + cFLDOSE_P150;

    return cFL_DOSEEFF;
  }

  private hasAnyAssumptionWithDose(
    dailyAssumptions: TrecVariableAssumption[] | undefined
  ): boolean {
    return !!dailyAssumptions?.find((d) => d.assumptions?.find((a) => a.quantity !== 0));
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    this.subscr.unsubscribe();
  }
}
